import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEnterOrSpace, KEYCODES } from '@Utils/KeyboardUtil';
import trim from 'lodash/trim';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import PropTypes from 'prop-types';
import Tooltip from '@Components/Tooltip';
import InputWithValidationMessage from '@Components/Input/InputWithValidationMessage';
import { ButtonWithIcon } from '@els/els-react--button';
import HoverTooltip from '@Components/HoverTooltip';
import { onProjectNameEdit } from '../RetroSynthesisProjects/analytics';

import './RsEditableItem.theme.scss';

function RsEditableItem({
  value: initialValue,
  onValueEdited,
  maxChars,
  showTruncationTooltip,
  validateValueInput,
  isEditMode,
  setEditMode,
  trackingContent,
}) {
  const [alternateValue, setAlternateValue] = useState(initialValue);
  const [displayTruncationTooltip, setDisplayTruncationTooltip] = useState(false);

  const inputRef = useRef(null);
  const labelRef = useRef(null);

  const doSave = useCallback(() => {
    if (!isEmpty(trim(alternateValue)) && alternateValue !== initialValue) {
      onValueEdited(alternateValue);
      onProjectNameEdit({ action: 'complete', trackingContent });
    } else {
      onProjectNameEdit({ action: 'abandoned', trackingContent });
    }

    setEditMode(false);
  }, [onValueEdited, alternateValue, initialValue, setEditMode, trackingContent]);

  const handleWrapperKeyDown = useCallback(
    (e) => {
      if (!isEditMode && isEnterOrSpace(e)) {
        e.stopPropagation();
        e.preventDefault();
        setEditMode(true);
      }
    },
    [isEditMode, setEditMode]
  );

  const handleInputKeyDown = useCallback(
    (e) => {
      if (e.keyCode === KEYCODES.ENTER) {
        doSave();
      }
      if (e.keyCode === KEYCODES.ESC) {
        setEditMode(false);
        onProjectNameEdit({ action: 'abandoned', trackingContent });
      }
    },
    [doSave, setEditMode, trackingContent]
  );

  const handleInputClickOutside = useCallback(
    (event) => {
      const inputElement = inputRef.current;

      if (inputElement && !inputElement.contains(event.target)) {
        doSave();
      }
    },
    [doSave]
  );

  const handleButtonClick = useMemo(
    () => () => {
      setEditMode(!isEditMode);
    },
    [isEditMode, setEditMode]
  );

  const inputProps = {
    onKeyDown: handleInputKeyDown,
    ref: inputRef,
  };
  if (maxChars) {
    set(inputProps, 'maxLength', maxChars);
  }

  const buttonId = useMemo(() => `toggle-edit-mode-${initialValue.replace(/\s/g, '-')}`, [initialValue]);

  // Effect to handle outside component click
  useEffect(() => {
    document.addEventListener('mousedown', handleInputClickOutside);

    return () => document.removeEventListener('mousedown', handleInputClickOutside);
  }, [handleInputClickOutside]);

  useEffect(() => {
    if (!isEditMode) {
      setAlternateValue(initialValue);
    }
  }, [isEditMode, initialValue]);

  useEffect(() => {
    const labelElement = labelRef.current;

    if (labelElement) {
      setDisplayTruncationTooltip(labelElement.scrollWidth > labelElement.clientWidth);
    } else {
      setDisplayTruncationTooltip(false);
    }
  }, [labelRef, initialValue]);

  useEffect(() => {
    if (displayTruncationTooltip) {
      Tooltip.rebuild();
    }
  }, [displayTruncationTooltip]);

  useEffect(() => {
    if (isEditMode) {
      onProjectNameEdit({ action: 'start', trackingContent });
    }
  }, [isEditMode, trackingContent]);

  const nonEditModeView =
    showTruncationTooltip && displayTruncationTooltip ? (
      <HoverTooltip tooltip={initialValue}>
        <ButtonWithIcon
          ref={labelRef}
          className="rs-editable-item-non-editable-button"
          type="link"
          onClick={handleButtonClick}
          sprite="Edit"
          size="small"
          iconRight
          linkIconSize="xs"
          aria-label="Edit input value">
          {initialValue}
        </ButtonWithIcon>
      </HoverTooltip>
    ) : (
      <ButtonWithIcon
        ref={labelRef}
        type="link"
        onClick={handleButtonClick}
        className="rs-editable-item-non-editable-button"
        sprite="Edit"
        size="small"
        iconRight
        linkIconSize="xs"
        aria-label="Edit input value">
        {initialValue}
      </ButtonWithIcon>
    );

  return (
    <div role="button" aria-labelledby={buttonId} tabIndex={0} className="rs-editable-item" onKeyDown={handleWrapperKeyDown}>
      {isEditMode ? (
        <>
          <InputWithValidationMessage
            className="rs-editable-item__input-wrapper"
            autofocus
            clearButton={false}
            value={initialValue}
            inputProps={inputProps}
            validateValueInput={validateValueInput}
            onChange={setAlternateValue}
            debounceDelay={0}
            preSelectText
          />
          <ButtonWithIcon
            ref={labelRef}
            type="link"
            onClick={handleButtonClick}
            className="rs-editable-item__close-button"
            sprite="Close"
            size="small"
            iconRight
            iconOnly
            linkIconSize="xs"
            aria-label="Leave input with changes">
            Close
          </ButtonWithIcon>
        </>
      ) : (
        nonEditModeView
      )}
    </div>
  );
}

RsEditableItem.defaultProps = {
  onValueEdited: noop,
  showTruncationTooltip: true,
  validateValueInput: () => true,
  isEditMode: false,
};

RsEditableItem.propTypes = {
  value: PropTypes.string,
  onValueEdited: PropTypes.func,
  maxChars: PropTypes.number,
  showTruncationTooltip: PropTypes.bool,
  validateValueInput: PropTypes.func,
  isEditMode: PropTypes.bool,
  setEditMode: PropTypes.func,
  trackingContent: PropTypes.array,
};

export default RsEditableItem;
